import type { TNotificationState } from "libs/design-system/src/types/notification/notification";

const DEFAULT_TOAST_TYPE = "info";
type TToast = TNotificationState | "error";

type DeepValue = string | { [key: string]: DeepValue };
const asGroupingCodes = <
  T extends Record<
    keyof typeof SYSTEM_EVENTS_SRC_FEATURES,
    DeepValue
  >
>(v: T) => v;

const toastTypeMap: Record<TToast, string> = {
  success: "success",
  danger: "error",
  error: "error",
  normal: DEFAULT_TOAST_TYPE,
  primary: DEFAULT_TOAST_TYPE,
  warning: DEFAULT_TOAST_TYPE,
};

export const SYSTEM_EVENTS = {
  TOAST_SHOWN: "Toast Shown"
};

export const SYSTEM_EVENTS_PARAMS = {
  TOAST_TYPE: "toast_type",
  TOAST_CODE: "toast_code",
  MESSAGE: "message",
  SRC_FEATURE: "source_feature",
};

export const SYSTEM_EVENTS_GROUPING_CODES = asGroupingCodes({
  USERS_AND_PERMS: {
    RESEND_INVITATION: "RESEND_INVITATION",
    EXISTING_PERMISSIONS_ROLE: "EXISTING_PERMISSIONS_ROLE",
    CREATE_ROLE: "CREATE_ROLE",
    UPDATE_ROLE: "UPDATE_ROLE",
    CHANGE_ROLE: "CHANGE_ROLE",
    INVITE_MEMBER: "INVITE_MEMBER",
    REMOVE_MEMBER: "REMOVE_MEMBER",
    CANCEL_INVITATION: "CANCEL_INVITATION",
    DELETE_ROLE: "DELETE_ROLE"
  },
  TRANSACTIONS: {
    DETAILS: {
      VOID_INITIATION: "VOID_INITIATION",
      CANCEL_TRANSACTION: "CANCEL_TRANSACTION",
      VOID_TRANSACTION: "VOID_TRANSACTION",
      CAPTURE_TRANSACTION: "CAPTURE_TRANSACTION",
      OTP_SUBMIT: "OTP_SUBMIT",
    }
  },
  PAYMENT_INTEGRATIONS: {
    DETAILS: {
      EDIT_INTEGRATION: "EDIT_INTEGRATION"
    }
  },
  RISK_CENTER: {
    MAIN: {
      SUBSCRIPTION: "SUBSCRIPTION"
    },
    DETAILS: {
      BLACKLIST: {
        DEACTIVATE: "BLACKLIST_DEACTIVATION",
        ACTIVATE: "BLACKLIST_ACTIVATION",
        DELETE: "BLACKLIST_DELETE",
        ADD: "BLACKLIST_ADD",
        EDIT: "BLACKLIST_EDIT",
      },
      WHITELIST: {
        DEACTIVATE: "WHITELIST_DEACTIVATION",
        ACTIVATE: "WHITELIST_ACTIVATION",
        DELETE: "WHITELIST_DELETE",
        ADD: "WHITELIST_ADD",
        EDIT: "WHITELIST_EDIT",
      },
      RULES: {
        DEACTIVATE: "RULES_DEACTIVATION",
        DELETE: "RULES_DELETE",
        ADD: "RULES_ADD",
        EDIT: "RULES_EDIT",
      }
    },
  },
  REPORTS: {
    STATEMENTS_TAB: {
      DOWNLOAD_STATEMENTS: "DOWNLOAD_STATEMENTS",
    },
    DIGEST_TAB: {
      DOWNLOAD_DIGEST: "DOWNLOAD_DIGEST",
    },
    REPORTS_TAB: {
      TYPES: "TYPES",
      DOWNLOAD: "DOWNLOAD",
      EXPORT: "EXPORT",
      COLUMNS: "COLUMNS",
      SECONDARY_FILTER: "SECONDARY_FILTER",
    }
  },
  REACH: {
    CAMPAIGNS: {
      CREATE_CAMPAIGN: "CREATE_CAMPAIGN",
      SENDER_NAME: "SENDER_NAME"
    },
    CAMPAIGN_DETAILS: {
      CAMPAIGN_TERMINATION: "CAMPAIGN_TERMINATION"
    }
  },
  API_KEYS: {
    GET: {
      PUBLIC_SECRET: "GET_PUBLIC_SECRET_KEY",
      API: "GET_API_KEY",
      HMAC: "GET_HMAC",
    },
    RECREATE: {
      API: "API_KEY_RECREATION",
      PUBLIC: "PUBLIC_KEY_RECREATION",
      SECRET: "SECRET_KEY_RECREATION",
      HMAC: "HMAC_RECREATION",
    },
  }
});

export const SYSTEM_EVENTS_SRC_FEATURES = {
  USERS_AND_PERMS: "Users and Permissions",
  TRANSACTIONS: {
    DETAILS: "Single Transaction Details",
  },
  PAYMENT_INTEGRATIONS: {
    DETAILS: "Single Integration Details",
  },
  RISK_CENTER: {
    MAIN: "Risk Center",
    DETAILS: "Risk Center Details",
  },
  REPORTS: {
    REPORTS_TAB: "Reports/ Reports Tab",
    STATEMENTS_TAB: "Reports/ Statements Tab",
    DIGEST_TAB: "Reports/ Digest Tab",
  },
  REACH: {
    CAMPAIGNS: "Reach/ Campaigns",
    CAMPAIGN_ANALYSIS: "Reach/ Campaign Analysis",
    CAMPAIGN_DETAILS: "Reach/ CAMPAIGN_DETAILS",
    BUNDLES: "Reach/ Bundles"
  },
  API_KEYS: "API Keys"
};

export const SYSTEM_EVENTS_VALUES = {
  TOAST_TYPE: (type?: TToast | null) => type ? toastTypeMap[type] : DEFAULT_TOAST_TYPE,
};